import { ColumnDef } from "@tanstack/react-table";
import { getLinkToWithNewSearchParameter } from "helpers";
import { useLanguage } from "hooks";
import { t } from "i18next";
/* eslint-disable react-hooks/exhaustive-deps */
import moment from "moment";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, ItemsTable, PersonInfo } from "ui";

import { IRefund } from "../../financial.types";
import { getReasonTextByType } from "../../helpers";
import { ConfirmRefundModal } from "../modals/ConfirmRefundModal";
import { RefundsTableProps } from "./RefundsTable.types";

import styles from "./RefundsTable.module.scss";

const RefundsTable = ({
  refunds,
  sorting,
  setSorting,
  isLoading,
  refetchRefunds
}: RefundsTableProps) => {
  const [searchParams] = useSearchParams();
  const { currentLanguage } = useLanguage();

  const [confirmingRefund, setConfirmingRefund] = useState<string | null>(null);

  const closeConfirmRefundModal = () => setConfirmingRefund(null);

  const columns: Array<ColumnDef<IRefund, IRefund[]>> = useMemo(
    () => [
      {
        id: "id",
        header: t("Order"),
        enableSorting: false,
        size: 10,
        minSize: 10,
        cell: ({ row }) => {
          const { phoneNumber, fullName, deliveryZone } =
            refunds[row.index].order.deliveryAddress;

          return (
            <>
              <p className={styles["refunds__item--id"]}>
                {refunds[row.index].orderId}
              </p>
              <div className={styles["refunds__item--details"]}>
                <pre>{`${fullName} `}</pre>
                <pre>{`${deliveryZone.country}, ${deliveryZone.city} `}</pre>
                <pre>{phoneNumber}</pre>
              </div>
            </>
          );
        }
      },
      {
        id: "reason",
        header: t("Reason"),
        enableSorting: false,
        size: 10,
        minSize: 10,
        cell: ({ row }) => (
          <p className={styles["refunds__item--reason"]}>
            {t(getReasonTextByType(refunds[row.index].type))}
          </p>
        )
      },
      {
        id: "reseller",
        header: t("Reseller"),
        enableSorting: false,
        size: 8,
        minSize: 8,
        cell: ({ row }) => (
          <PersonInfo
            dimensions={{ width: 24, height: 24, borderRadius: "50%" }}
            image={{ url: row.original.reseller.image?.url }}
            nameClassName={styles["refunds__item--reseller"]}
            wrapperClassName={styles["refunds__item--reseller-wrapper"]}
            personName={`${row.original.reseller.firstname} ${row.original.reseller.lastname}`}
          />
        )
      },
      {
        id: "amount",
        accessorKey: "amount",
        header: t("Amount"),
        size: 6,
        minSize: 6,
        cell: ({ row }) => (
          <p>{row.original.amount.toLocaleString("en-US")} FCFA</p>
        )
      },
      {
        id: "createdAt",
        header: t("Refund date"),
        size: 8,
        minSize: 8,
        cell: ({ row }) => (
          <pre>
            {`${moment(refunds[row.index].createdAt).format("DD/MM/YYYY")}`}
            {"\n"}
            {`${moment(refunds[row.index].createdAt).format("HH:mm")}`}
          </pre>
        )
      },
      {
        id: "status",
        header: t("Status"),
        enableSorting: false,
        size: 5,
        minSize: 5,
        cell: ({ row }) =>
          !refunds[row.index].isRefunded ? (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setConfirmingRefund(refunds[row.index].id);
              }}
            >
              {t("Confirm")}
            </Button>
          ) : (
            <p className={styles["refunds__item--refund-sent"]}>
              {t("Refund sent")}
            </p>
          )
      }
    ],
    [refunds, currentLanguage]
  );

  return (
    <>
      <ItemsTable
        columns={columns}
        data={refunds ?? []}
        sorting={sorting}
        skeletonHeight={90}
        getLinkUrl={({ id }) => {
          return getLinkToWithNewSearchParameter({
            searchParams,
            parameterName: "refundId",
            parameterValue: id
          });
        }}
        setSorting={setSorting}
        isLoading={isLoading}
        className={styles.refunds__table}
      />

      {confirmingRefund && (
        <ConfirmRefundModal
          refundId={confirmingRefund}
          isOpen={!!confirmingRefund}
          onClose={closeConfirmRefundModal}
          onSuccess={refetchRefunds}
        />
      )}
    </>
  );
};

export default RefundsTable;
